import { computed, Directive, input } from '@angular/core';

type ButtonSize = 'XS' | 'S' | 'M' | 'L';
type ButtonVariant = 'DEFAULT' | '270' | 'CODE' | 'WARN';

const BUTTON_CLASS_BY_SIZE: Record<ButtonSize, string[]> = {
	XS: ['p-0h-rl', 'p-0h-tb', 'br-0t'],
	S: ['p-0h-rl', 'p-0h-tb', 'br-1'],
	M: ['p-0t-rl', 'p-0t-tb', 'br-1'],
	L: ['p-1-rl', 'p-1-tb', 'br-1'],
};

@Directive({
	selector: '[buttonIcon]',
	standalone: true,
	host: {
		class: 'fl fl-ai-c fl-jc-fs fs-m',
		'[class]': '$hostClassMap()',
		'[attr.disabled]': '$disabled() ? "true" : null',
		style: '--spacing: 8px; transition: background-color 300ms, color 300ms;',
	},
})
export class ButtonIconDirective {
	public readonly $size = input<ButtonSize>('M', { alias: 'size' });
	public readonly $active = input(false, { alias: 'active' });
	public readonly $variant = input<ButtonVariant>('DEFAULT', {
		alias: 'variant',
	});

	public readonly $disabled = input<boolean, boolean | 'true' | 'false'>(false, {
		alias: 'disabled',
		transform: (value) => {
			return `${value}` === `true`;
		},
	});

	protected readonly $hostClassMap = computed(() => {
		const variant = this.$variant();
		const active = this.$active();
		const disabled = this.$disabled();
		const canHover = !disabled && !active;

		// Size classes
		const classMap: Record<string, boolean> = {};
		for (const cls of BUTTON_CLASS_BY_SIZE[this.$size()] ?? []) {
			classMap[cls] = true;
		}

		// Text color (default state)
		classMap['fc-400'] = !active && variant === 'DEFAULT';
		classMap['fc-500'] = !active && variant === '270';
		classMap['fc-primary'] = active;
		classMap['fc-100'] = variant === 'CODE';
		classMap['fc-warn'] = variant === 'WARN';

		// Text color (hover)
		classMap['fc-primary-500-on-hover'] = canHover && variant === 'CODE';
		classMap['fc-800-on-hover'] = canHover && variant !== 'CODE';

		// Background (default)
		classMap['bg-standard-hover-on-hover'] = canHover && variant === 'DEFAULT';
		classMap['bg-270'] = variant === '270';
		classMap['bg-standard-hover-over-270-on-hover'] = canHover && variant === '270';
		classMap['bg-code'] = variant === 'CODE';
		classMap['bg-primary-20-on-hover'] = canHover && variant === 'CODE';
		classMap['bg-warn-20-on-hover'] = canHover && variant === 'WARN';
		classMap['bg-primary-20'] = active;

		return classMap;
	});
}
